import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { optimizeImageForUpload, PHOTO_PRESET } from '@/lib/imageOptimize';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Loader2, IdCard, Calendar, DollarSign, Rocket, Sparkles, Target, MapPin, Activity,
  Upload, X, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import { supabase } from '@/lib/supabase';
import { ADMIN_AI_ENABLED } from '@/lib/featureFlags';
import { FestivalVenuePinPicker } from '@/components/FestivalVenuePinPicker';
import { PIN_STYLES, PIN_STYLE_KEYS, type PinStyleKey } from '@/lib/pinStyles';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Mirror of the row shape returned by `ad_campaigns`. Only includes the
 * subset of columns this dialog can read or write. All optional so we can
 * accept thin Campaign objects coming from the list view.
 */
export interface EditableCampaign {
  id: string;
  brand_name: string;
  slug?: string | null;
  placement: string;
  audience?: 'all' | 'passenger' | 'driver' | null;
  active: boolean;
  is_paused?: boolean | null;
  is_house_ad?: boolean | null;
  starts_at: string | null;
  ends_at: string | null;
  priority?: number | null;
  // Creative
  headline?: string | null;
  body?: string | null;
  cta_text?: string | null;
  cta_url: string | null;
  offer_code?: string | null;
  offer_terms?: string | null;
  image_url: string | null;
  background_color?: string | null;
  video_url?: string | null;
  creative_image_url?: string | null;
  logo_url?: string | null;
  creative_mode?: string | null;
  // Targeting
  corridor_ids?: string[] | null;
  hour_start?: number | null;
  hour_end?: number | null;
  weather_triggers?: string[] | null;
  target_radius_km?: number | null;
  event_slug?: string | null;
  event_location_label?: string | null;
  // Map pin
  pin_lat_override?: number | null;
  pin_lng_override?: number | null;
  pin_style?: string | null;
  // Pricing & pacing
  bid_xcd?: number | null;
  cpm_rate_xcd?: number | null;
  flat_fee_xcd?: number | null;
  max_impressions_per_day_per_user?: number | null;
  cool_down_hours?: number | null;
  max_daily_impressions?: number | null;
  // Billing / contract
  invoice_status?: string | null;
  invoice_amount_xcd?: number | null;
  invoice_period_start?: string | null;
  invoice_period_end?: string | null;
  contract_url?: string | null;
  contract_notes?: string | null;
  // Linkage
  listing_id?: string | null;
}

interface EditCampaignDialogProps {
  campaign: EditableCampaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once after a successful save so the parent list can refetch. */
  onUpdated: () => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** Must match the public.ad_placement enum exactly. */
const PLACEMENTS: Array<{ value: string; label: string }> = [
  { value: 'passenger_home_header', label: 'Passenger Home Header' },
  { value: 'commute_morning_takeover', label: 'Morning Commute Takeover' },
  { value: 'commute_evening_takeover', label: 'Evening Commute Takeover' },
  { value: 'driver_idle_strip', label: 'Driver Idle Strip' },
  { value: 'ride_complete_full', label: 'Ride-Complete Full Screen' },
  { value: 'weather_alert_banner', label: 'Weather / Alert Banner' },
  { value: 'map_pin_featured', label: 'Map Pin (Featured)' },
  { value: 'house_inbound_lead', label: 'House Inbound Lead' },
  { value: 'festival_event_takeover', label: 'Festival Takeover' },
  { value: 'splash_takeover', label: 'Splash Takeover' },
];

const AUDIENCES: Array<{ value: 'all' | 'passenger' | 'driver'; label: string }> = [
  { value: 'all', label: 'Everyone (passengers + drivers)' },
  { value: 'passenger', label: 'Passengers only' },
  { value: 'driver', label: 'Drivers only' },
];

const INVOICE_STATUSES = [
  'pending', 'invoiced', 'paid', 'overdue', 'cancelled',
] as const;

/** Must match public.ad_creative_mode enum. */
const CREATIVE_MODES: Array<{ value: string; label: string; hint: string }> = [
  { value: 'ai_generated',      label: 'AI generated',      hint: 'Copy + art produced by the wizard.' },
  { value: 'admin_authored',    label: 'Admin authored',    hint: 'Operator wrote the copy by hand.' },
  { value: 'client_image',      label: 'Client image',      hint: 'Advertiser supplied a logo card.' },
  { value: 'client_full_bleed', label: 'Client full-bleed', hint: 'Advertiser supplied a full-bleed image.' },
  { value: 'client_video',      label: 'Client video',      hint: 'Advertiser supplied a short MP4.' },
];

/** Max character lengths the mobile UI is built around. Going over isn't
 *  blocked (DB has no constraint) but the counter turns red so operators
 *  know they're risking a clipped render. */
const CREATIVE_LIMITS = { headline: 32, body: 110, ctaText: 14 };

/** Weather triggers the resolver understands. Operators pick the
 *  conditions under which a weather-aware campaign should serve. */
const WEATHER_OPTIONS = [
  'rain', 'heavy_rain', 'thunderstorm', 'cloudy', 'clear', 'hot', 'cold',
];

/**
 * Map-style placements get the dedicated "Map & Pin" tab. For the rest the
 * tab is hidden to keep the dialog tight.
 */
const MAP_PLACEMENTS = new Set(['map_pin_featured', 'festival_event_takeover']);

// ─── Helpers ────────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function dateInputToIsoStart(s: string): string | null {
  // 00:00 AST = 04:00 UTC
  return s ? `${s}T04:00:00Z` : null;
}

function dateInputToIsoEnd(s: string): string | null {
  return s ? `${s}T23:59:59-04:00` : null;
}

function nullable<T>(v: T | undefined | null | ''): T | null {
  if (v === undefined || v === null || v === '') return null;
  return v as T;
}

function parseNum(v: string | number | null | undefined): number | null {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Compute the JSON-encoded set of fields that actually changed between
 * the original campaign row and the operator's edits. We only PATCH the
 * delta — leaving untouched columns untouched protects the audit log
 * from drowning in noise and keeps the diff-toast meaningful.
 */
function computeDiff(
  original: EditableCampaign,
  edits: Record<string, unknown>,
): { changes: Record<string, unknown>; summary: string[] } {
  const changes: Record<string, unknown> = {};
  const summary: string[] = [];

  for (const [k, v] of Object.entries(edits)) {
    const before = (original as any)[k];
    const beforeNorm = before === undefined ? null : before;
    const afterNorm = v === undefined ? null : v;

    // Date columns: stored as TIMESTAMPTZ but compared as date-only here.
    if (k === 'starts_at' || k === 'ends_at') {
      const b = beforeNorm ? String(beforeNorm).slice(0, 10) : '';
      const a = afterNorm ? String(afterNorm).slice(0, 10) : '';
      if (b !== a) {
        changes[k] = afterNorm;
        summary.push(`${k.replace('_', ' ')}: ${b || '∞'} → ${a || '∞'}`);
      }
      continue;
    }

    // Array columns
    if (Array.isArray(beforeNorm) || Array.isArray(afterNorm)) {
      const b = JSON.stringify(beforeNorm ?? []);
      const a = JSON.stringify(afterNorm ?? []);
      if (b !== a) {
        changes[k] = afterNorm;
        summary.push(`${k}: ${b} → ${a}`);
      }
      continue;
    }

    if (beforeNorm !== afterNorm) {
      changes[k] = afterNorm;
      const beforeLabel = beforeNorm === null ? '–' : String(beforeNorm);
      const afterLabel = afterNorm === null ? '–' : String(afterNorm);
      summary.push(`${k}: ${beforeLabel} → ${afterLabel}`);
    }
  }

  return { changes, summary };
}

/** Fields whose change should prompt the operator to force-push the
 *  campaign so users see the update immediately rather than waiting for
 *  the next natural fetch cycle. */
const FORCE_PUSH_TRIGGER_FIELDS = new Set([
  'headline', 'body', 'cta_text', 'cta_url', 'offer_code', 'offer_terms', 'image_url', 'background_color',
  'video_url', 'creative_image_url', 'creative_mode', 'placement', 'audience',
  'starts_at', 'ends_at', 'is_house_ad', 'active', 'is_paused',
  'pin_lat_override', 'pin_lng_override', 'pin_style',
]);

// ─── Form state ─────────────────────────────────────────────────────────────

interface FormState {
  // Identity
  brand_name: string;
  slug: string;
  placement: string;
  audience: 'all' | 'passenger' | 'driver';
  is_house_ad: boolean;
  active: boolean;
  is_paused: boolean;
  priority: string;
  // Creative
  headline: string;
  body: string;
  cta_text: string;
  cta_url: string;
  offer_code: string;
  offer_terms: string;
  image_url: string;
  background_color: string;
  video_url: string;
  creative_image_url: string;
  logo_url: string;
  creative_mode: string;
  // Targeting
  corridor_ids: string[];
  hour_start: string;
  hour_end: string;
  weather_triggers: string[];
  target_radius_km: string;
  // Map & Pin (only meaningful for map_pin_featured / festival_event_takeover)
  pin_lat_override: string;
  pin_lng_override: string;
  pin_style: string;
  event_slug: string;
  event_location_label: string;
  // Schedule & Pacing
  start_date: string;
  end_date: string;
  max_impressions_per_day_per_user: string;
  cool_down_hours: string;
  max_daily_impressions: string;
  // Billing
  bid_xcd: string;
  cpm_rate_xcd: string;
  flat_fee_xcd: string;
  invoice_status: string;
  invoice_amount_xcd: string;
  invoice_period_start: string;
  invoice_period_end: string;
  contract_url: string;
  contract_notes: string;
}

function hydrate(c: EditableCampaign): FormState {
  return {
    brand_name: c.brand_name ?? '',
    slug: c.slug ?? '',
    placement: c.placement ?? 'passenger_home_header',
    audience: (c.audience ?? 'all') as 'all' | 'passenger' | 'driver',
    is_house_ad: !!c.is_house_ad,
    active: c.active,
    is_paused: !!c.is_paused,
    priority: c.priority != null ? String(c.priority) : '0',
    headline: c.headline ?? '',
    body: c.body ?? '',
    cta_text: c.cta_text ?? '',
    cta_url: c.cta_url ?? '',
    offer_code: c.offer_code ?? '',
    offer_terms: c.offer_terms ?? '',
    image_url: c.image_url ?? '',
    background_color: c.background_color ?? '',
    video_url: c.video_url ?? '',
    creative_image_url: c.creative_image_url ?? '',
    logo_url: c.logo_url ?? '',
    creative_mode: c.creative_mode ?? 'ai_generated',
    corridor_ids: Array.isArray(c.corridor_ids) ? [...c.corridor_ids] : [],
    hour_start: c.hour_start != null ? String(c.hour_start) : '',
    hour_end: c.hour_end != null ? String(c.hour_end) : '',
    weather_triggers: Array.isArray(c.weather_triggers) ? [...c.weather_triggers] : [],
    target_radius_km: c.target_radius_km != null ? String(c.target_radius_km) : '',
    pin_lat_override: c.pin_lat_override != null ? String(c.pin_lat_override) : '',
    pin_lng_override: c.pin_lng_override != null ? String(c.pin_lng_override) : '',
    pin_style: c.pin_style ?? '',
    event_slug: c.event_slug ?? '',
    event_location_label: c.event_location_label ?? '',
    start_date: isoToDateInput(c.starts_at),
    end_date: isoToDateInput(c.ends_at),
    max_impressions_per_day_per_user:
      c.max_impressions_per_day_per_user != null
        ? String(c.max_impressions_per_day_per_user) : '',
    cool_down_hours:
      c.cool_down_hours != null ? String(c.cool_down_hours) : '',
    max_daily_impressions:
      c.max_daily_impressions != null ? String(c.max_daily_impressions) : '',
    bid_xcd: c.bid_xcd != null ? String(c.bid_xcd) : '',
    cpm_rate_xcd: c.cpm_rate_xcd != null ? String(c.cpm_rate_xcd) : '',
    flat_fee_xcd: c.flat_fee_xcd != null ? String(c.flat_fee_xcd) : '',
    invoice_status: c.invoice_status ?? '',
    invoice_amount_xcd:
      c.invoice_amount_xcd != null ? String(c.invoice_amount_xcd) : '',
    invoice_period_start: isoToDateInput(c.invoice_period_start),
    invoice_period_end: isoToDateInput(c.invoice_period_end),
    contract_url: c.contract_url ?? '',
    contract_notes: c.contract_notes ?? '',
  };
}

function payloadFromForm(f: FormState): Record<string, unknown> {
  const isFree = !f.cpm_rate_xcd && !f.flat_fee_xcd && !f.bid_xcd;
  return {
    brand_name: f.brand_name,
    slug: f.slug || slugify(f.brand_name),
    placement: f.placement,
    audience: f.audience,
    is_house_ad: f.is_house_ad || isFree,
    active: f.active,
    is_paused: f.is_paused,
    priority: parseNum(f.priority) ?? 0,
    headline: nullable(f.headline),
    body: nullable(f.body),
    cta_text: nullable(f.cta_text),
    cta_url: nullable(f.cta_url),
    offer_code: nullable(f.offer_code.trim().toUpperCase()),
    offer_terms: nullable(f.offer_terms),
    image_url: nullable(f.image_url),
    background_color: nullable(f.background_color),
    video_url: nullable(f.video_url),
    creative_image_url: nullable(f.creative_image_url),
    logo_url: nullable(f.logo_url),
    creative_mode: nullable(f.creative_mode) ?? 'ai_generated',
    corridor_ids: f.corridor_ids.length > 0 ? f.corridor_ids : null,
    hour_start: parseNum(f.hour_start),
    hour_end: parseNum(f.hour_end),
    weather_triggers: f.weather_triggers.length > 0 ? f.weather_triggers : null,
    target_radius_km: parseNum(f.target_radius_km),
    pin_lat_override: parseNum(f.pin_lat_override),
    pin_lng_override: parseNum(f.pin_lng_override),
    pin_style: nullable(f.pin_style),
    event_slug: nullable(f.event_slug),
    event_location_label: nullable(f.event_location_label),
    starts_at: dateInputToIsoStart(f.start_date),
    ends_at: dateInputToIsoEnd(f.end_date),
    max_impressions_per_day_per_user: parseNum(f.max_impressions_per_day_per_user),
    cool_down_hours: parseNum(f.cool_down_hours),
    max_daily_impressions: parseNum(f.max_daily_impressions),
    bid_xcd: parseNum(f.bid_xcd),
    cpm_rate_xcd: parseNum(f.cpm_rate_xcd),
    flat_fee_xcd: parseNum(f.flat_fee_xcd),
    invoice_status: nullable(f.invoice_status),
    invoice_amount_xcd: parseNum(f.invoice_amount_xcd),
    invoice_period_start: nullable(f.invoice_period_start),
    invoice_period_end: nullable(f.invoice_period_end),
    contract_url: nullable(f.contract_url),
    contract_notes: nullable(f.contract_notes),
  };
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Full-tab editor for an existing campaign. Surfaces every column the
 * wizard writes to `ad_campaigns`, computes a delta on save (so the audit
 * log only records real changes), and offers a "Push update to live users"
 * follow-up when the operator changes a user-visible field.
 *
 * Tabs other than the three baseline ones (Creative, Targeting, Map & Pin,
 * Activity) are stubbed with a "Coming soon" panel here and filled in by
 * follow-up commits to keep this initial change reviewable.
 */
export default function EditCampaignDialog({
  campaign, open, onOpenChange, onUpdated,
}: EditCampaignDialogProps) {
  const { seesMoney, isManager } = useAdminAuth();
  const [form, setForm] = useState<FormState | null>(null);
  const [tab, setTab] = useState<string>('identity');
  const [submitting, setSubmitting] = useState(false);
  const [forcePushAfter, setForcePushAfter] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Live-fetched corridor list for the Targeting tab. We hydrate on
  // first open and then keep the cache for the life of the dialog.
  const [corridors, setCorridors] = useState<Array<{ id: string; name: string }>>([]);
  const [corridorsLoading, setCorridorsLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setCorridorsLoading(true);
      try {
        const { data, error } = await supabase
          .from('corridors')
          .select('id, name')
          .eq('is_active', true)
          .eq('is_draft', false)
          .order('name');
        if (cancelled) return;
        if (error) throw error;
        setCorridors((data as Array<{ id: string; name: string }>) || []);
      } catch {
        // Targeting still works without corridors — operator just
        // can't multi-select. Fail silently rather than break the dialog.
      } finally {
        if (!cancelled) setCorridorsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // ── Linked listing coords (for the Map tab) ───────────────────────
  // When the campaign is linked to a local_business_listing AND has no
  // pin_lat_override, the mobile app falls back to the brand's
  // registered lat/lng to render the pin. We mirror that resolution
  // here so the Map tab opens with the picker already centred on the
  // pin's CURRENT effective location, instead of a blank Dominica view
  // that hides which spot is actually in production. We only hit the
  // DB once per dialog open per campaign.
  const [listingCoords, setListingCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [listingCoordsResolved, setListingCoordsResolved] = useState(false);
  useEffect(() => {
    if (!open) {
      setListingCoords(null);
      setListingCoordsResolved(false);
      return;
    }
    if (!campaign?.listing_id) {
      // No linked listing → nothing to fetch, mark resolved immediately
      // so the picker can mount with whatever override (or null) we have.
      setListingCoords(null);
      setListingCoordsResolved(true);
      return;
    }
    let cancelled = false;
    setListingCoordsResolved(false);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('local_business_listings')
          .select('latitude, longitude')
          .eq('id', campaign.listing_id)
          .maybeSingle();
        if (cancelled) return;
        if (!error && data) {
          const lat = typeof data.latitude === 'number' ? data.latitude : null;
          const lng = typeof data.longitude === 'number' ? data.longitude : null;
          if (lat != null && lng != null) {
            setListingCoords({ lat, lng });
          }
        }
      } catch {
        // Non-fatal — picker falls back to Dominica default.
      } finally {
        if (!cancelled) setListingCoordsResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, campaign?.listing_id]);

  // ── Activity tab data ──────────────────────────────────────────────
  // Lazily fetched the first time the operator clicks the Activity
  // tab. Two queries in parallel: (a) last-30d ad_events aggregated
  // by event_type, (b) last 20 admin_audit_log rows for this campaign.
  interface ActivityStats {
    impressions: number;
    clicks: number;
    dismisses: number;
    conversions: number;
  }
  interface AuditRow {
    id: string;
    action: string;
    created_at: string;
    payload: Record<string, unknown> | null;
    admin_id: string | null;
  }
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activity, setActivity] = useState<ActivityStats | null>(null);
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  useEffect(() => {
    if (tab !== 'activity' || !campaign || activityLoaded) return;
    let cancelled = false;
    (async () => {
      setActivityLoading(true);
      try {
        const since = new Date(Date.now() - 30 * 86400000).toISOString();
        // (a) Per-event-type counts
        const eventTypes = ['impression', 'click', 'dismiss', 'conversion'] as const;
        const stats: ActivityStats = {
          impressions: 0, clicks: 0, dismisses: 0, conversions: 0,
        };
        await Promise.all(eventTypes.map(async (t) => {
          const { count, error } = await supabase
            .from('ad_events')
            .select('id', { count: 'exact', head: true })
            .eq('campaign_id', campaign.id)
            .eq('event_type', t)
            .gte('created_at', since);
          if (error) return;
          const v = count ?? 0;
          if (t === 'impression') stats.impressions = v;
          if (t === 'click') stats.clicks = v;
          if (t === 'dismiss') stats.dismisses = v;
          if (t === 'conversion') stats.conversions = v;
        }));

        // (b) Audit log
        const { data: auditData, error: auditErr } = await supabase
          .from('admin_audit_log')
          .select('id, action, created_at, payload, admin_id')
          .eq('target_table', 'ad_campaigns')
          .eq('target_id', campaign.id)
          .order('created_at', { ascending: false })
          .limit(20);
        if (cancelled) return;
        if (!auditErr && auditData) setAuditRows(auditData as AuditRow[]);

        if (!cancelled) {
          setActivity(stats);
          setActivityLoaded(true);
        }
      } catch {
        // Best-effort — leaves the panel in its loading state if
        // events/audit RLS denies.
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, campaign?.id, activityLoaded]);

  // Reset activity cache when a different campaign opens.
  useEffect(() => {
    setActivityLoaded(false);
    setActivity(null);
    setAuditRows([]);
  }, [campaign?.id]);

  // Re-hydrate every time a new campaign opens.
  useEffect(() => {
    if (open && campaign) {
      setForm(hydrate(campaign));
      setTab('identity');
      setForcePushAfter(false);
    }
  }, [open, campaign]);

  const isMapPlacement = useMemo(
    () => form ? MAP_PLACEMENTS.has(form.placement) : false,
    [form],
  );

  const diff = useMemo(() => {
    if (!campaign || !form) return { changes: {}, summary: [] };
    return computeDiff(campaign, payloadFromForm(form));
  }, [campaign, form]);

  const triggersForcePushPrompt = useMemo(() => {
    return Object.keys(diff.changes).some((k) => FORCE_PUSH_TRIGGER_FIELDS.has(k));
  }, [diff]);

  if (!form || !campaign) return null;

  const update = (patch: Partial<FormState>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  /**
   * Calls the existing `generate-sponsor-creative` Edge Function and
   * stuffs the returned headline/body/CTA into the Creative tab. We
   * reuse the same placement→template mapping as EditCopyDialog so
   * tone stays consistent across both surfaces.
   */
  const handleAiRewrite = async () => {
    if (!form) return;
    setAiGenerating(true);
    try {
      const placementToTemplate: Record<string, string> = {
        passenger_home_header: 'hotel_monthly',
        commute_morning_takeover: 'telecom_commute',
        commute_evening_takeover: 'telecom_commute',
        ride_complete_full: 'restaurant_evening',
        weather_alert_banner: 'ferry_weather',
        map_pin_featured: 'shop_local_pin',
        festival_event_takeover: 'festival_takeover',
        splash_takeover: 'launch_splash',
      };
      const templateKey = placementToTemplate[form.placement] || 'hotel_monthly';
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/generate-sponsor-creative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${anonKey}` },
        body: JSON.stringify({
          brand_name: form.brand_name,
          brand_description: '',
          template_key: templateKey,
        }),
      });
      if (!res.ok) throw new Error('AI service error');
      const json = await res.json();
      update({
        headline: json.headline || form.headline || form.brand_name,
        body: json.body || form.body,
        cta_text: json.cta_text || form.cta_text || 'Learn more',
      });
      if (json.ai_used === false) {
        toast.info('The AI copywriter is offline. Your fields are still editable.');
      } else {
        toast.success('AI rewrote headline, body and CTA');
      }
    } catch (err: any) {
      toast.error(err.message || 'AI rewrite failed');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.brand_name.trim()) {
      toast.error('Brand name is required');
      return;
    }
    if (Object.keys(diff.changes).length === 0) {
      toast.info('No changes to save');
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.updateCampaign(campaign.id, diff.changes);
      toast.success(
        `Saved · ${Object.keys(diff.changes).length} field${
          Object.keys(diff.changes).length === 1 ? '' : 's'
        } updated`,
        {
          description: diff.summary.slice(0, 3).join(' • '),
          duration: 6000,
        },
      );

      if (forcePushAfter && triggersForcePushPrompt) {
        try {
          const { data, error } = await supabase.rpc(
            'admin_force_push_campaign', { p_campaign_id: campaign.id },
          );
          if (error) throw error;
          const res = data as any;
          if (res?.ok === false) {
            toast.warning(`Saved, but force-push failed: ${res.error}`);
          } else if (res?.will_serve === false) {
            toast.warning(`Pushed, but won't serve until you fix: ${res.warning}`, { duration: 8000 });
          } else {
            toast.success(`Pushed to live users (wiped ${res?.dismissals_wiped ?? 0} dismissals)`);
          }
        } catch (err: any) {
          toast.warning(`Saved, but force-push failed: ${err.message || err}`);
        }
      }

      onUpdated();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save campaign');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-4xl w-[min(960px,96vw)] max-h-[92vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-gray-800">
          <DialogTitle className="text-white flex items-center gap-2">
            Edit Campaign
            <Badge variant="outline" className="text-[10px] border-gray-700 text-gray-400 font-mono">
              {campaign.slug || campaign.id.slice(0, 8)}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-xs">
            {campaign.brand_name} · {campaign.placement.replace(/_/g, ' ')}
            {campaign.is_paused && (
              <Badge className="ml-2 bg-amber-500/10 text-amber-400 border-0 text-[10px]">Paused</Badge>
            )}
            {!campaign.active && !campaign.is_paused && (
              <Badge className="ml-2 bg-gray-700/50 text-gray-400 border-gray-600 text-[10px]">Inactive</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={setTab}
          className="flex-1 overflow-hidden flex flex-col"
        >
          {/* Tab strip — flex with min-widths so the labels never crash
              into the icons. At very narrow widths it scrolls
              horizontally rather than crushing the buttons together. */}
          <div className="mx-6 mt-3 flex-shrink-0 overflow-x-auto">
            <TabsList className="bg-gray-800/60 border border-gray-800 h-9 inline-flex w-auto min-w-full gap-1">
              <TabsTrigger value="identity" className="text-xs gap-1.5 px-3 min-w-[100px] data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                <IdCard className="w-3.5 h-3.5" />
                Identity
              </TabsTrigger>
              <TabsTrigger value="creative" className="text-xs gap-1.5 px-3 min-w-[100px] data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                <Sparkles className="w-3.5 h-3.5" />
                Creative
              </TabsTrigger>
              <TabsTrigger value="targeting" className="text-xs gap-1.5 px-3 min-w-[100px] data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                <Target className="w-3.5 h-3.5" />
                Targeting
              </TabsTrigger>
              <TabsTrigger
                value="map"
                className="text-xs gap-1.5 px-3 min-w-[80px] data-[state=active]:bg-gray-700 data-[state=active]:text-white"
                disabled={!isMapPlacement}
              >
                <MapPin className="w-3.5 h-3.5" />
                Map
              </TabsTrigger>
              <TabsTrigger value="schedule" className="text-xs gap-1.5 px-3 min-w-[100px] data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                <Calendar className="w-3.5 h-3.5" />
                Schedule
              </TabsTrigger>
              {seesMoney && (
                <TabsTrigger value="billing" className="text-xs gap-1.5 px-3 min-w-[90px] data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                  <DollarSign className="w-3.5 h-3.5" />
                  Billing
                </TabsTrigger>
              )}
              <TabsTrigger value="activity" className="text-xs gap-1.5 px-3 min-w-[100px] data-[state=active]:bg-gray-700 data-[state=active]:text-white">
                <Activity className="w-3.5 h-3.5" />
                Activity
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {/* ── Identity ─────────────────────────────────────── */}
            <TabsContent value="identity" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Brand name</Label>
                  <Input
                    value={form.brand_name}
                    onChange={(e) => {
                      const name = e.target.value;
                      update({
                        brand_name: name,
                        slug: form.slug || slugify(name),
                      });
                    }}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Slug</Label>
                  <Input
                    value={form.slug}
                    onChange={(e) => update({ slug: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white font-mono text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Placement</Label>
                  <Select value={form.placement} onValueChange={(v) => update({ placement: v })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-800">
                      {PLACEMENTS.map((p) => (
                        <SelectItem key={p.value} value={p.value} className="text-gray-300 focus:bg-gray-800 focus:text-white">
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Audience</Label>
                  <Select value={form.audience} onValueChange={(v) => update({ audience: v as any })}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-800">
                      {AUDIENCES.map((a) => (
                        <SelectItem key={a.value} value={a.value} className="text-gray-300 focus:bg-gray-800 focus:text-white">
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2 col-span-1">
                  <Label className="text-gray-300">Priority</Label>
                  <Input
                    type="number"
                    step="1"
                    value={form.priority}
                    onChange={(e) => update({ priority: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                  <p className="text-[10px] text-gray-500">Higher wins ties when multiple campaigns compete for the same placement.</p>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => update({ active: v })}
                    disabled={!isManager && campaign.active}
                    aria-label="Active"
                  />
                  <div>
                    <Label className="text-gray-300">Active</Label>
                    {!isManager && campaign.active && (
                      <p className="text-[10px] text-gray-500">Only the owner and directors can switch off a live campaign. Use Paused instead.</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Switch
                    checked={form.is_paused}
                    onCheckedChange={(v) => update({ is_paused: v })}
                  />
                  <Label className="text-gray-300">Paused</Label>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={form.is_house_ad}
                  onCheckedChange={(v) => update({ is_house_ad: v })}
                />
                <div>
                  <Label className="text-gray-300">House ad</Label>
                  <p className="text-[10px] text-gray-500">House ads always lose to paid campaigns in the bidding auction.</p>
                </div>
              </div>
            </TabsContent>

            {/* ── Creative ───────────────────────────────────── */}
            <TabsContent value="creative" className="space-y-4 mt-0">
              <div className="flex items-center justify-between">
                <h4 className="text-xs uppercase tracking-wider text-gray-500">Copy</h4>
                {ADMIN_AI_ENABLED && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleAiRewrite}
                    disabled={aiGenerating || !form.brand_name.trim()}
                    className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 h-7 text-xs gap-1"
                  >
                    {aiGenerating
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Sparkles className="w-3 h-3" />}
                    Rewrite with AI
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Headline</Label>
                  <span className={`text-[10px] tabular-nums ${form.headline.length > CREATIVE_LIMITS.headline ? 'text-red-400' : 'text-gray-500'}`}>
                    {form.headline.length}/{CREATIVE_LIMITS.headline}
                  </span>
                </div>
                <Input
                  value={form.headline}
                  onChange={(e) => update({ headline: e.target.value })}
                  className="bg-gray-800 border-gray-700 text-white font-semibold"
                  placeholder="e.g. Sunday brunch is on"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Body</Label>
                  <span className={`text-[10px] tabular-nums ${form.body.length > CREATIVE_LIMITS.body ? 'text-red-400' : 'text-gray-500'}`}>
                    {form.body.length}/{CREATIVE_LIMITS.body}
                  </span>
                </div>
                <Textarea
                  value={form.body}
                  onChange={(e) => update({ body: e.target.value })}
                  rows={3}
                  className="bg-gray-800 border-gray-700 text-white resize-none"
                  placeholder="Short marketing copy shown under the headline."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300">CTA text</Label>
                    <span className={`text-[10px] tabular-nums ${form.cta_text.length > CREATIVE_LIMITS.ctaText ? 'text-red-400' : 'text-gray-500'}`}>
                      {form.cta_text.length}/{CREATIVE_LIMITS.ctaText}
                    </span>
                  </div>
                  <Input
                    value={form.cta_text}
                    onChange={(e) => update({ cta_text: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="See rates"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">CTA URL</Label>
                  <Input
                    value={form.cta_url}
                    onChange={(e) => update({ cta_url: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="https://…"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Deal code (optional)</Label>
                  <Input
                    value={form.offer_code}
                    onChange={(e) => update({ offer_code: e.target.value.toUpperCase().replace(/\s+/g, '').slice(0, 24) })}
                    className="bg-gray-800 border-gray-700 text-white font-mono"
                    placeholder="HPLATE10"
                  />
                  <p className="text-[11px] text-gray-500">Riders show it at the counter. Ask the business how many they took.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Deal terms</Label>
                  <Input
                    value={form.offer_terms}
                    onChange={(e) => update({ offer_terms: e.target.value.slice(0, 200) })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="10% off mains. Until Oct 31."
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <h4 className="text-xs uppercase tracking-wider text-gray-500">Visuals</h4>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Creative mode</Label>
                    <Select value={form.creative_mode} onValueChange={(v) => update({ creative_mode: v })}>
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-800">
                        {CREATIVE_MODES.map((m) => (
                          <SelectItem key={m.value} value={m.value} className="text-gray-300 focus:bg-gray-800 focus:text-white">
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-gray-500">
                      {CREATIVE_MODES.find((m) => m.value === form.creative_mode)?.hint}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Background color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={form.background_color || '#0f3d2e'}
                        onChange={(e) => update({ background_color: e.target.value })}
                        className="bg-gray-800 border-gray-700 w-12 h-9 p-1 cursor-pointer"
                      />
                      <Input
                        value={form.background_color}
                        onChange={(e) => update({ background_color: e.target.value })}
                        placeholder="#0f3d2e"
                        className="bg-gray-800 border-gray-700 text-white font-mono text-sm flex-1"
                      />
                    </div>
                  </div>
                </div>

                <ImageAssetField
                  label="Hero image"
                  value={form.image_url}
                  onChange={(v) => update({ image_url: v })}
                  hint="Main creative shown in sheet carousels and full-bleed cards."
                />
                <ImageAssetField
                  label="Client full-bleed image"
                  value={form.creative_image_url}
                  onChange={(v) => update({ creative_image_url: v })}
                  hint="Used only when Creative mode is set to Client full-bleed."
                />
                <ImageAssetField
                  label="Logo"
                  value={form.logo_url}
                  onChange={(v) => update({ logo_url: v })}
                  hint="Small brand mark. Falls back to the brand's first letter if missing."
                />

                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Video URL (.mp4)</Label>
                  <Input
                    value={form.video_url}
                    onChange={(e) => update({ video_url: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="https://…/sponsor.mp4"
                  />
                  {form.video_url && (
                    <video
                      key={form.video_url}
                      src={form.video_url}
                      controls
                      muted
                      className="mt-1 max-h-40 w-full rounded border border-gray-800 bg-black"
                    />
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ── Targeting ──────────────────────────────────── */}
            <TabsContent value="targeting" className="space-y-5 mt-0">
              {/* Placement — also lives on the Identity tab; surfaced
                  here too because changing the placement is a targeting
                  decision (it controls WHERE the ad appears). Both tabs
                  edit the same `form.placement` so there is no state to
                  duplicate. */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs uppercase tracking-wider text-gray-500">Placement (where it appears)</h4>
                  <span className="text-[11px] text-gray-500">
                    Changing this can move the pin / change the surface
                  </span>
                </div>
                <Select value={form.placement} onValueChange={(v) => update({ placement: v })}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                    <SelectValue placeholder="Select placement" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700 text-white">
                    {PLACEMENTS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">
                  Map / festival placements unlock the dedicated <strong className="text-gray-400">Map & Pin</strong> tab so you can re-position the pin.
                </p>
              </div>

              {/* Time-of-day window */}
              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs uppercase tracking-wider text-gray-500">Time-of-day window</h4>
                  <span className="text-[11px] text-gray-500">
                    Leave blank for 24/7
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-gray-300 text-sm">Start hour (0–23)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={form.hour_start}
                      onChange={(e) => update({ hour_start: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-300 text-sm">End hour (0–24)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={24}
                      value={form.hour_end}
                      onChange={(e) => update({ hour_end: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="24"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-gray-500">
                  Example: 6 → 10 only serves the morning commute. The
                  resolver ignores the window when both fields are blank.
                </p>
              </div>

              {/* Corridor multi-select */}
              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs uppercase tracking-wider text-gray-500">Corridors</h4>
                  <span className="text-[11px] text-gray-500">
                    {form.corridor_ids.length === 0
                      ? 'Showing on all corridors'
                      : `${form.corridor_ids.length} selected`}
                  </span>
                </div>
                {corridorsLoading ? (
                  <p className="text-xs text-gray-500">Loading corridors…</p>
                ) : corridors.length === 0 ? (
                  <p className="text-xs text-gray-500">No corridors found, so the campaign will run platform-wide.</p>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {corridors.map((c) => {
                      const checked = form.corridor_ids.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => update({
                            corridor_ids: checked
                              ? form.corridor_ids.filter((x) => x !== c.id)
                              : [...form.corridor_ids, c.id],
                          })}
                          className={`text-left text-xs rounded-md border px-2.5 py-1.5 transition-colors ${
                            checked
                              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                              : 'bg-gray-800/60 border-gray-800 text-gray-400 hover:text-gray-200 hover:border-gray-700'
                          }`}
                        >
                          {c.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                {form.corridor_ids.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => update({ corridor_ids: [] })}
                    className="text-[11px] text-gray-400 hover:text-white h-6 px-2"
                  >
                    Clear (show everywhere)
                  </Button>
                )}
              </div>

              {/* Weather triggers */}
              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs uppercase tracking-wider text-gray-500">Weather triggers</h4>
                  <span className="text-[11px] text-gray-500">
                    {form.weather_triggers.length === 0 ? 'No weather filter' : `${form.weather_triggers.length} active`}
                  </span>
                </div>
                {/* Live status. The mobile apps poll the Open-Meteo
                    feed for Roseau every 15 minutes and pass the
                    resolved condition (rain / heavy_rain / thunderstorm
                    / cloudy / clear / hot / cold) to the ad resolver,
                    so triggers picked here DO gate serving end-to-end.
                    Operators should pick MULTIPLE triggers when they
                    want broad coverage — e.g. for an umbrella ad,
                    select rain + heavy_rain + thunderstorm. */}
                <div className="flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
                  <div className="text-[11px] leading-relaxed text-emerald-100/90">
                    <strong className="text-emerald-200">Live.</strong> The mobile app reads Dominica's
                    weather every 15 minutes and feeds it to the ad resolver. Pick the conditions you want
                    this campaign to fire on. For broad coverage (e.g. umbrella ads) tick multiple triggers,
                    because the resolver matches on exact label equality.
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {WEATHER_OPTIONS.map((w) => {
                    const checked = form.weather_triggers.includes(w);
                    return (
                      <button
                        key={w}
                        type="button"
                        onClick={() => update({
                          weather_triggers: checked
                            ? form.weather_triggers.filter((x) => x !== w)
                            : [...form.weather_triggers, w],
                        })}
                        className={`text-xs rounded-full border px-2.5 py-0.5 capitalize ${
                          checked
                            ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                            : 'bg-gray-800/60 border-gray-800 text-gray-400 hover:text-gray-200 hover:border-gray-700'
                        }`}
                      >
                        {w.replace(/_/g, ' ')}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-500">
                  The campaign only serves when the live weather matches
                  one of these triggers. Useful for "umbrella delivery"
                  or "ferry-cancelled" placements.
                </p>
              </div>

              {/* Proximity radius */}
              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs uppercase tracking-wider text-gray-500">Proximity radius</h4>
                  <span className="text-[11px] text-gray-500">
                    {form.target_radius_km ? `${form.target_radius_km} km` : 'Default 8 km'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    value={form.target_radius_km}
                    onChange={(e) => update({ target_radius_km: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white w-28"
                    placeholder="8"
                  />
                  <span className="text-xs text-gray-500">km</span>
                </div>
                <p className="text-[10px] text-gray-500">
                  Only matters for ads with a location (a linked business
                  pin). The ad serves to riders within this radius of the
                  business, by current location on the idle screen or by
                  destination on the confirm/active screens. Blank = 8&nbsp;km
                  default. Island-wide brand ads ignore this.
                </p>
              </div>
            </TabsContent>

            {/* ── Map & Pin ──────────────────────────────────── */}
            <TabsContent value="map" className="space-y-5 mt-0">
              {!isMapPlacement ? (
                <p className="text-xs text-gray-500">
                  This tab only applies to map-pin and festival placements.
                </p>
              ) : (
                <>
                  {/* Interactive pin picker. We pre-center on the
                      pin's CURRENT effective location:
                        1. pin_lat_override / pin_lng_override (explicit
                           operator pick — highest priority, matches the
                           DB resolver's behaviour)
                        2. Linked listing's lat/lng (what mobile renders
                           when no override is set)
                        3. null → picker defaults to Dominica overview
                      This means an operator editing an existing pin
                      sees it instantly instead of having to hunt for
                      it on a blank map. */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs uppercase tracking-wider text-gray-500">Pin location</h4>
                      <span className="text-[11px] text-gray-500">
                        Drag the pin or click the map · search by venue
                      </span>
                    </div>
                    {(() => {
                      const overrideLat = parseNum(form.pin_lat_override);
                      const overrideLng = parseNum(form.pin_lng_override);
                      const usingOverride = overrideLat != null && overrideLng != null;
                      const effLat = overrideLat ?? listingCoords?.lat ?? null;
                      const effLng = overrideLng ?? listingCoords?.lng ?? null;
                      const captionTone = usingOverride
                        ? 'text-emerald-300'
                        : effLat != null
                          ? 'text-sky-300'
                          : 'text-gray-500';
                      const caption = usingOverride
                        ? `Showing campaign override at ${effLat!.toFixed(4)}, ${effLng!.toFixed(4)}.`
                        : effLat != null
                          ? `No override. Showing brand's registered location at ${effLat.toFixed(4)}, ${effLng!.toFixed(4)}. Drag or click to set a campaign-specific override.`
                          : "No location yet. Click anywhere on Dominica to drop this campaign's pin.";
                      return (
                        <>
                          <div className={`text-[11px] ${captionTone} flex items-center gap-1.5`}>
                            <MapPin className="w-3 h-3" />
                            {caption}
                          </div>
                          {/* Defer mounting the picker until the linked
                              listing's coords have resolved. The picker
                              picks its initial centre/zoom from the lat
                              & lng props ONCE on mount; if we render it
                              before the listing fetch resolves, it
                              would lock onto the wide Dominica view and
                              never re-zoom when the coords arrive a
                              moment later. */}
                          {!listingCoordsResolved ? (
                            <div className="h-[320px] rounded-md border border-gray-800 bg-gray-950 flex items-center justify-center text-xs text-gray-500">
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading map…
                            </div>
                          ) : (
                            <FestivalVenuePinPicker
                              lat={effLat}
                              lng={effLng}
                              themeColor={form.background_color || PIN_STYLES[(form.pin_style as PinStyleKey) || 'other'].bg}
                              emoji={PIN_STYLES[(form.pin_style as PinStyleKey) || 'other'].emoji}
                              label={form.event_location_label || form.brand_name}
                              onChange={(lat, lng) => update({
                                pin_lat_override: lat.toFixed(6),
                                pin_lng_override: lng.toFixed(6),
                              })}
                            />
                          )}
                        </>
                      );
                    })()}
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div className="space-y-1">
                        <Label className="text-gray-400 text-xs">Latitude</Label>
                        <Input
                          value={form.pin_lat_override}
                          onChange={(e) => update({ pin_lat_override: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono text-xs"
                          placeholder="15.301"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-gray-400 text-xs">Longitude</Label>
                        <Input
                          value={form.pin_lng_override}
                          onChange={(e) => update({ pin_lng_override: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono text-xs"
                          placeholder="-61.388"
                        />
                      </div>
                    </div>
                    {(form.pin_lat_override || form.pin_lng_override) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => update({ pin_lat_override: '', pin_lng_override: '' })}
                        className="text-[11px] text-gray-400 hover:text-white h-6 px-2"
                      >
                        Clear override (use brand's registered coords)
                      </Button>
                    )}
                  </div>

                  {/* Pin style picker */}
                  <div className="pt-4 border-t border-gray-800/80 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs uppercase tracking-wider text-gray-500">Pin style</h4>
                      <span className="text-[11px] text-gray-500">
                        {form.pin_style
                          ? PIN_STYLES[form.pin_style as PinStyleKey]?.label
                          : 'Auto (from category)'}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 max-h-44 overflow-y-auto pr-1">
                      {PIN_STYLE_KEYS.map((k) => {
                        const style = PIN_STYLES[k];
                        const Icon = style.icon;
                        const active = form.pin_style === k;
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => update({ pin_style: active ? '' : k })}
                            className={`flex flex-col items-center gap-1 rounded-md border p-2 transition-colors ${
                              active
                                ? 'border-emerald-500/50 bg-emerald-500/10'
                                : 'border-gray-800 bg-gray-800/50 hover:border-gray-700'
                            }`}
                            title={style.description}
                          >
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: style.bg, color: style.fg }}
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </div>
                            <span className="text-[10px] text-gray-300 truncate w-full text-center">
                              {style.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    {form.pin_style && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => update({ pin_style: '' })}
                        className="text-[11px] text-gray-400 hover:text-white h-6 px-2"
                      >
                        Reset to auto (uses brand category)
                      </Button>
                    )}
                  </div>

                  {/* Event/festival metadata */}
                  <div className="pt-4 border-t border-gray-800/80 space-y-3">
                    <h4 className="text-xs uppercase tracking-wider text-gray-500">Event metadata</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-gray-300 text-sm">Event slug</Label>
                        <Input
                          value={form.event_slug}
                          onChange={(e) => update({ event_slug: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white font-mono text-sm"
                          placeholder="dominica-carnival-2026"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-gray-300 text-sm">Location label</Label>
                        <Input
                          value={form.event_location_label}
                          onChange={(e) => update({ event_location_label: e.target.value })}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Windsor Park Stadium"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            {/* ── Schedule & Pacing ──────────────────────────────── */}
            <TabsContent value="schedule" className="space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300">Start date</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => update({ start_date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">End date</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => update({ end_date: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <h4 className="text-xs uppercase tracking-wider text-gray-500">Pacing & frequency caps</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Per-user / day</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.max_impressions_per_day_per_user}
                      onChange={(e) => update({ max_impressions_per_day_per_user: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="5"
                    />
                    <p className="text-[10px] text-gray-500">Max times a single user sees this in 24h.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Cool-down (hours)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.cool_down_hours}
                      onChange={(e) => update({ cool_down_hours: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="24"
                    />
                    <p className="text-[10px] text-gray-500">Hours to wait after a dismiss before re-showing.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Max impressions / day</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.max_daily_impressions}
                      onChange={(e) => update({ max_daily_impressions: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                      placeholder="∞"
                    />
                    <p className="text-[10px] text-gray-500">Global cap. Blank = unlimited.</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ── Billing & Contract ─────────────────────────────── */}
            {seesMoney && <TabsContent value="billing" className="space-y-4 mt-0">
              <h4 className="text-xs uppercase tracking-wider text-gray-500">Bidding model</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Bid (XCD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.bid_xcd}
                    onChange={(e) => update({ bid_xcd: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">CPM (XCD / 1k imps)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.cpm_rate_xcd}
                    onChange={(e) => update({ cpm_rate_xcd: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Flat fee (XCD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.flat_fee_xcd}
                    onChange={(e) => update({ flat_fee_xcd: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <h4 className="text-xs uppercase tracking-wider text-gray-500">Invoice</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Status</Label>
                    <Select
                      value={form.invoice_status || '__none__'}
                      onValueChange={(v) => update({ invoice_status: v === '__none__' ? '' : v })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                        <SelectValue placeholder="–" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-800">
                        <SelectItem value="__none__" className="text-gray-300 focus:bg-gray-800">None</SelectItem>
                        {INVOICE_STATUSES.map((s) => (
                          <SelectItem key={s} value={s} className="text-gray-300 focus:bg-gray-800 focus:text-white">
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Amount (XCD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.invoice_amount_xcd}
                      onChange={(e) => update({ invoice_amount_xcd: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Period start</Label>
                    <Input
                      type="date"
                      value={form.invoice_period_start}
                      onChange={(e) => update({ invoice_period_start: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-300 text-sm">Period end</Label>
                    <Input
                      type="date"
                      value={form.invoice_period_end}
                      onChange={(e) => update({ invoice_period_end: e.target.value })}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-gray-800/80 space-y-3">
                <h4 className="text-xs uppercase tracking-wider text-gray-500">Contract</h4>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Contract URL</Label>
                  <Input
                    value={form.contract_url}
                    onChange={(e) => update({ contract_url: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="https://..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300 text-sm">Notes</Label>
                  <Input
                    value={form.contract_notes}
                    onChange={(e) => update({ contract_notes: e.target.value })}
                    className="bg-gray-800 border-gray-700 text-white"
                    placeholder="Internal notes (not shown to users)"
                  />
                </div>
              </div>
            </TabsContent>}

            {/* ── Activity ──────────────────────────────────── */}
            <TabsContent value="activity" className="space-y-5 mt-0">
              <h4 className="text-xs uppercase tracking-wider text-gray-500">Performance · last 30 days</h4>

              {activityLoading && !activity ? (
                <div className="grid grid-cols-4 gap-2">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-16 rounded-md bg-gray-800/50 animate-pulse" />
                  ))}
                </div>
              ) : activity ? (
                <>
                  <div className="grid grid-cols-4 gap-2">
                    <StatCell label="Impressions" value={activity.impressions} />
                    <StatCell
                      label="Clicks"
                      value={activity.clicks}
                      sub={
                        activity.impressions > 0
                          ? `${((activity.clicks / activity.impressions) * 100).toFixed(2)}% CTR`
                          : '–'
                      }
                    />
                    <StatCell label="Dismisses" value={activity.dismisses} tone={activity.dismisses > 0 ? 'warn' : undefined} />
                    <StatCell label="Conversions" value={activity.conversions} tone={activity.conversions > 0 ? 'good' : undefined} />
                  </div>

                  {/* Estimated spend, only shown when we have a billing model. */}
                  {(campaign.cpm_rate_xcd || campaign.flat_fee_xcd) && (
                    <div className="rounded-md border border-gray-800 bg-gray-800/30 px-3 py-2 text-xs text-gray-300 flex items-center justify-between">
                      <span>Estimated spend (30d)</span>
                      <span className="font-mono text-emerald-300">
                        {(() => {
                          const cpmPart = campaign.cpm_rate_xcd
                            ? (activity.impressions / 1000) * Number(campaign.cpm_rate_xcd)
                            : 0;
                          const flatPart = campaign.flat_fee_xcd
                            ? Number(campaign.flat_fee_xcd)
                            : 0;
                          const total = cpmPart + flatPart;
                          return `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        })()}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-gray-500">No data yet. Refresh after the campaign has served.</p>
              )}

              <div className="pt-4 border-t border-gray-800/80 space-y-2">
                <h4 className="text-xs uppercase tracking-wider text-gray-500">Audit log · last 20 changes</h4>
                {auditRows.length === 0 ? (
                  <p className="text-xs text-gray-500">No audit entries recorded for this campaign yet.</p>
                ) : (
                  <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
                    {auditRows.map((row) => (
                      <li
                        key={row.id}
                        className="rounded-md border border-gray-800/80 bg-gray-800/30 px-2.5 py-1.5 text-[11px]"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-gray-200 font-medium">{row.action}</span>
                          <span className="text-gray-500 font-mono shrink-0">
                            {new Date(row.created_at).toLocaleString()}
                          </span>
                        </div>
                        {row.payload && Object.keys(row.payload).length > 0 && (
                          <p className="text-gray-500 mt-0.5 font-mono truncate">
                            {Object.keys(row.payload).slice(0, 5).join(', ')}
                            {Object.keys(row.payload).length > 5 ? ` +${Object.keys(row.payload).length - 5}` : ''}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="px-6 py-4 border-t border-gray-800 flex flex-row items-center justify-between gap-4 flex-shrink-0">
          <div className="text-[11px] text-gray-500 flex-1 truncate">
            {diff.summary.length === 0 ? (
              <span>No changes</span>
            ) : (
              <span>
                <span className="text-emerald-400 font-medium">{diff.summary.length}</span>
                {' field'}
                {diff.summary.length === 1 ? '' : 's'}
                {' changed'}
                {diff.summary.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="ml-1 underline decoration-dotted underline-offset-2 cursor-help">
                        (preview)
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-gray-800 border-gray-700 text-xs max-w-sm">
                      <ul className="space-y-0.5">
                        {diff.summary.slice(0, 8).map((s, i) => (
                          <li key={i} className="font-mono text-[10px]">{s}</li>
                        ))}
                        {diff.summary.length > 8 && (
                          <li className="text-gray-400 italic text-[10px]">
                            +{diff.summary.length - 8} more…
                          </li>
                        )}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
            )}
          </div>

          {triggersForcePushPrompt && (
            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300">
              <Switch
                checked={forcePushAfter}
                onCheckedChange={setForcePushAfter}
              />
              <Rocket className="w-3 h-3 text-amber-400" />
              <span>Push to live users after save</span>
            </label>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-gray-400"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={submitting || diff.summary.length === 0 || !form.brand_name.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Compact KPI cell used by the Activity tab. `tone` colors the number
 * for the dismiss / conversion lines where the sign of the number
 * matters (red = bad, green = good).
 */
function StatCell({
  label, value, sub, tone,
}: {
  label: string;
  value: number;
  sub?: string;
  tone?: 'warn' | 'good';
}) {
  const valueColor =
    tone === 'warn' ? 'text-red-300' :
    tone === 'good' ? 'text-emerald-300' :
    'text-white';
  return (
    <div className="rounded-md border border-gray-800 bg-gray-800/30 p-2">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 truncate">{label}</p>
      <p className={`mt-0.5 text-lg font-bold tabular-nums ${valueColor}`}>
        {value.toLocaleString()}
      </p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * Image asset field with full self-service: paste a URL, upload a new
 * file from disk, or remove the existing one. Uploads go to the
 * `sponsor-creatives` public bucket (same one the wizard's Upload tab
 * uses) so operators don't need to host files anywhere else.
 *
 * Used for every image slot on the Creative tab: hero image, client
 * full-bleed creative, and logo.
 */
function ImageAssetField({
  label, value, onChange, hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    // Match the existing sponsor-creatives bucket constraints (10 MB cap).
    const MAX_BYTES = 10 * 1024 * 1024;
    const ACCEPTED = /^image\/(png|jpeg|webp|svg\+xml|gif)$/;
    if (!ACCEPTED.test(file.type)) {
      toast.error('Image must be PNG, JPEG, WEBP, SVG, or GIF.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`);
      return;
    }
    setUploading(true);
    try {
      const optimized = await optimizeImageForUpload(file, PHOTO_PRESET);
      const ext = optimized.name.split('.').pop()?.toLowerCase() || 'png';
      const path = `campaign-edits/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('sponsor-creatives')
        .upload(path, optimized, {
          contentType: optimized.type,
          upsert: false,
          cacheControl: '31536000',
        });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('sponsor-creatives').getPublicUrl(path);
      onChange(urlData.publicUrl);
      toast.success(`Uploaded · ${label}`);
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-gray-300 text-sm">{label}</Label>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            className="text-[11px] h-6 px-2 text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10 border border-emerald-500/30"
          >
            {uploading
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Upload className="w-3 h-3 mr-1" />}
            {value ? 'Replace' : 'Upload'}
          </Button>
          {value && !uploading && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onChange('')}
              className="text-[11px] h-6 px-2 text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-red-500/30"
            >
              <X className="w-3 h-3 mr-1" />
              Remove
            </Button>
          )}
        </div>
      </div>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-gray-800 border-gray-700 text-white text-xs font-mono"
        placeholder="https://… or click Upload to pick a file"
      />
      {hint && <p className="text-[10px] text-gray-500">{hint}</p>}
      {value && (
        <div className="mt-1 rounded border border-gray-800 bg-gray-950 p-2 relative">
          <img
            src={value}
            alt={label}
            className="max-h-28 mx-auto object-contain rounded"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
}
